import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import api from "../lib/api";

export const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const tRef = useRef(null);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    clearTimeout(tRef.current);
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?q=${encodeURIComponent(q)}&limit=8`);
        setResults(data.items || []);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(tRef.current);
  }, [q]);

  if (!open) return null;

  return (
    <div
      data-testid="search-overlay"
      className="fixed inset-0 z-50 bg-jow-navy/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-jow-cream max-w-3xl mx-auto mt-24 border border-jow-gold/40"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-5 border-b border-jow-navy/10">
          <Search size={18} className="text-jow-gold" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search jhumkas, kundan, bridal…"
            className="flex-1 bg-transparent py-5 text-lg outline-none placeholder:text-jow-navy/40"
            data-testid="search-input"
          />
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-jow-navy/55 hover:text-jow-pink"
            data-testid="search-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {loading && (
            <p className="px-5 py-4 label-overline text-jow-navy/55">Searching…</p>
          )}
          {!loading && q && results.length === 0 && (
            <p className="px-5 py-8 text-center text-jow-navy/55" data-testid="search-empty">
              No matches yet.
            </p>
          )}
          {results.map((p) => (
            <Link
              key={p.slug}
              to={`/product/${p.slug}`}
              onClick={onClose}
              className="flex items-center gap-4 px-5 py-3 hover:bg-white border-b border-jow-navy/5"
              data-testid={`search-result-${p.slug}`}
            >
              <img src={p.image} alt={p.name} className="w-14 h-14 object-cover" />
              <div className="flex-1">
                <p className="font-display text-base text-jow-navy">{p.name}</p>
                <p className="text-xs text-jow-navy/55 capitalize">{p.category_slug?.replace("-", " ")}</p>
              </div>
              <span className="text-sm font-semibold text-jow-navy">
                ₹{p.price_inr.toLocaleString("en-IN")}
              </span>
            </Link>
          ))}
          {q && results.length > 0 && (
            <Link
              to={`/shop?q=${encodeURIComponent(q)}`}
              onClick={onClose}
              className="block px-5 py-4 text-center label-overline text-jow-pink hover:underline"
              data-testid="search-view-all"
            >
              View all results →
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
